import React, { useCallback, useRef } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  Background,
  MiniMap,
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  NodeTypes,
  useReactFlow,
  BackgroundVariant,
  ConnectionLineType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Box, Typography, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { adminTheme } from "theme/adminTheme";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PaymentIcon from "@mui/icons-material/Payment";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DrawIcon from "@mui/icons-material/Draw";
import DescriptionIcon from "@mui/icons-material/Description";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import StepNode, { StepNodeData } from "./StepNode";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import MouseIcon from "@mui/icons-material/Mouse";

interface FlowCanvasProps {
  initialNodes: Node<StepNodeData>[];
  initialEdges: Edge[];
  onNodesChange: (nodes: Node<StepNodeData>[]) => void;
  onNodeSelect: (nodeId: string | null) => void;
  selectedNodeId: string | null;
  onAddStep: (stepType: string) => void;
  availableStepTypes: string[];
}

// Map step types to icons
const getStepTypeIcon = (stepType: string) => {
  const icons: Record<string, React.ReactNode> = {
    DATA_INPUT: <AssignmentIcon />,
    PAYMENT: <PaymentIcon />,
    STRIPE_PAYMENT: <CreditCardIcon />,
    SIGN: <DrawIcon />,
    GENERATE_DOCUMENT: <DescriptionIcon />,
    NOTIFICATION: <NotificationsIcon />,
    CONFIRMATION: <CheckCircleIcon />,
    IDENTIFICATION: <PersonIcon />,
    DATA_OUTPUT: <CloudUploadIcon />,
  };
  return icons[stepType] || <AssignmentIcon />;
};

// Map step types to Lithuanian labels
const getStepTypeLabel = (stepType: string) => {
  const labels: Record<string, string> = {
    DATA_INPUT: "Duomenų įvedimas",
    PAYMENT: "Mokėjimas",
    STRIPE_PAYMENT: "Stripe mokėjimas",
    SIGN: "Pasirašymas",
    GENERATE_DOCUMENT: "Dokumento generavimas",
    NOTIFICATION: "Pranešimas",
    CONFIRMATION: "Patvirtinimas",
    IDENTIFICATION: "Identifikacija",
    DATA_OUTPUT: "Duomenų išvedimas",
  };
  return labels[stepType] || stepType;
};

const nodeTypes: NodeTypes = {
  stepNode: StepNode,
};

function FlowCanvasInner({
  initialNodes,
  initialEdges,
  onNodesChange,
  onNodeSelect,
  selectedNodeId,
  onAddStep,
  availableStepTypes,
}: FlowCanvasProps) {
  const [nodes, setNodes, onNodesChangeInternal] = useNodesState<Node<StepNodeData>>(initialNodes);
  const [edges, setEdges, onEdgesChangeInternal] = useEdgesState(initialEdges);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  useReactFlow();
  const isDraggingRef = useRef(false);
  const initializedRef = useRef(false);
  const [contextMenu, setContextMenu] = React.useState<{ x: number; y: number } | null>(null);

  // Initialize nodes and edges, preserving positions of existing nodes
  React.useEffect(() => {
    if (!isDraggingRef.current) {
      setNodes((currentNodes) => {
        // If we have current nodes, preserve their positions
        if (currentNodes.length > 0 && initialNodes.length === currentNodes.length) {
          // Map existing positions to new nodes
          return initialNodes.map((newNode, index) => {
            const existingNode = currentNodes.find(n => n.id === newNode.id);
            return {
              ...newNode,
              position: existingNode ? existingNode.position : newNode.position,
            };
          });
        }
        // Otherwise, use initial nodes as-is (first load or node count changed)
        return initialNodes;
      });
      setEdges(initialEdges);
      initializedRef.current = true;
    }
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Handle node changes - only notify parent on drag/drop, not on every change
  const handleNodesChange = useCallback(
    (changes: any) => {
      onNodesChangeInternal(changes);
    },
    [onNodesChangeInternal]
  );

  // Handle edge connection
  const onConnect = useCallback(
    (connection: any) => {
      setEdges((eds) => {
        // Add new edge with animation
        const newEdge: Edge = {
          ...connection,
          id: `edge-${connection.source}-${connection.target}`,
          type: "smoothstep",
          animated: true,
          style: { stroke: adminTheme.colors.primary, strokeWidth: 2 },
        };
        return [...eds, newEdge];
      });
    },
    [setEdges]
  );

  // Handle edge deletion
  const onEdgesDelete = useCallback(
    (edgesToDelete: Edge[]) => {
      setEdges((eds) =>
        eds.filter((edge) => !edgesToDelete.find((e) => e.id === edge.id))
      );
    },
    [setEdges]
  );

  // Handle node drag start
  const onNodeDragStart = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  // Handle node drag stop - reorder steps based on Y position
  const onNodeDragStop = useCallback(
    (event: React.MouseEvent, node: Node) => {
      setTimeout(() => {
        isDraggingRef.current = false;
      }, 100);

      // Get current nodes and sort by Y position to determine new order
      const sortedNodes = [...nodes].sort((a, b) => a.position.y - b.position.y);

      // Check if order has changed by comparing step indices
      const orderChanged = sortedNodes.some((n, idx) => n.data.stepIndex !== idx);

      if (orderChanged) {
        // Rebuild edges to connect nodes in new order
        const newEdges: Edge[] = [];
        for (let i = 0; i < sortedNodes.length - 1; i++) {
          newEdges.push({
            id: `edge-${sortedNodes[i].id}-${sortedNodes[i + 1].id}`,
            source: sortedNodes[i].id,
            target: sortedNodes[i + 1].id,
            type: "smoothstep",
            animated: true,
            style: { stroke: adminTheme.colors.primary, strokeWidth: 2 },
          });
        }
        setEdges(newEdges);

        // Notify parent to reorder steps in Formik
        // Pass sorted nodes (by Y position) with original IDs
        onNodesChange(sortedNodes);
      }
    },
    [nodes, onNodesChange, setEdges]
  );

  // Handle node click
  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      onNodeSelect(node.id);
    },
    [onNodeSelect]
  );

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    onNodeSelect(null);
  }, [onNodeSelect]);

  // Handle context menu
  const onPaneContextMenu = useCallback((event: React.MouseEvent | MouseEvent) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
    });
  }, []);

  // Handle context menu close
  const handleContextMenuClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Handle step type selection from context menu
  const handleStepTypeSelect = useCallback(
    (stepType: string) => {
      onAddStep(stepType);
      setContextMenu(null);
    },
    [onAddStep]
  );

  // Handle drag over
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  // Handle drop - add new step
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const stepType = event.dataTransfer.getData("application/reactflow-steptype");

      if (!stepType || !reactFlowWrapper.current) {
        return;
      }

      // Call parent to add step to Formik
      onAddStep(stepType);

      // The node will be added via the initialNodes prop update
    },
    [onAddStep]
  );

  return (
    <Box
      ref={reactFlowWrapper}
      sx={{
        flex: 1,
        height: "100%",
        bgcolor: adminTheme.colors.backgroundAlt,
        position: "relative",
        "& .react-flow__attribution": {
          display: "none",
        },
        "& .react-flow__panel.bottom.left": {
          display: "none",
        },
      }}
    >
      <ReactFlow
        nodes={nodes.map((node) => ({
          ...node,
          selected: node.id === selectedNodeId,
        }))}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChangeInternal}
        onConnect={onConnect}
        onEdgesDelete={onEdgesDelete}
        onNodeClick={onNodeClick}
        onNodeDragStart={onNodeDragStart}
        onNodeDragStop={onNodeDragStop}
        onPaneClick={onPaneClick}
        onPaneContextMenu={onPaneContextMenu}
        onDrop={onDrop}
        onDragOver={onDragOver}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{
          padding: 0.2,
          includeHiddenNodes: false,
        }}
        minZoom={0.1}
        maxZoom={1.5}
        defaultEdgeOptions={{
          type: "smoothstep",
          animated: true,
          style: { stroke: adminTheme.colors.primary, strokeWidth: 2 },
        }}
        nodesDraggable={true}
        nodesConnectable={true}
        elementsSelectable={true}
        selectNodesOnDrag={false}
        panOnDrag={true}
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomOnDoubleClick={true}
        connectionLineType={ConnectionLineType.SmoothStep}
        connectionLineStyle={{ stroke: adminTheme.colors.primary, strokeWidth: 2 }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color={adminTheme.colors.border}
        />
        <Controls
          showZoom={true}
          showFitView={true}
          showInteractive={false}
          position="bottom-right"
          style={{
            borderRadius: adminTheme.borderRadius.md,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: adminTheme.shadows.sm,
          }}
        />
        <MiniMap
          nodeColor={(node) => {
            const stepNode = node as Node<StepNodeData>;
            const colors: Record<string, string> = {
              DATA_INPUT: "#2196F3",
              PAYMENT: "#4CAF50",
              STRIPE_PAYMENT: "#6772E5",
              SIGN: "#9C27B0",
              GENERATE_DOCUMENT: "#FF9800",
              NOTIFICATION: "#F44336",
              CONFIRMATION: "#FFC107",
              IDENTIFICATION: "#616161",
              DATA_OUTPUT: "#00BCD4",
            };
            return colors[stepNode.data?.stepType] || "#757575";
          }}
          maskColor="rgba(0, 0, 0, 0.08)"
          position="bottom-left"
          style={{
            backgroundColor: adminTheme.colors.background,
            border: `1px solid ${adminTheme.colors.border}`,
            borderRadius: adminTheme.borderRadius.md,
          }}
        />
      </ReactFlow>

      {/* Enhanced empty state */}
      {nodes.length === 0 && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
            zIndex: 1000,
          }}
        >
          {/* Main card */}
          <Box
            sx={{
              bgcolor: adminTheme.colors.background,
              p: 5,
              borderRadius: adminTheme.borderRadius.xl,
              boxShadow: adminTheme.shadows.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              maxWidth: 420,
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: adminTheme.borderRadius.lg,
                background: adminTheme.gradients.primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
                boxShadow: `0 8px 24px ${adminTheme.colors.primary}40`,
              }}
            >
              <AccountTreeIcon sx={{ color: "white", fontSize: 40 }} />
            </Box>

            {/* Title */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: adminTheme.colors.textPrimary,
                mb: 1.5,
              }}
            >
              Pradėkite kurti procesą
            </Typography>

            {/* Description */}
            <Typography
              variant="body2"
              sx={{
                color: adminTheme.colors.textSecondary,
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              Sukurkite savo automatizuotą procesą naudodami kontekstinį meniu
            </Typography>

            {/* Instructions */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                textAlign: "left",
              }}
            >
              {[
                {
                  icon: <MouseIcon sx={{ fontSize: 20 }} />,
                  text: "Spustelėkite dešinį pelės klavišą darbo srityje",
                },
                {
                  icon: <AddIcon sx={{ fontSize: 20 }} />,
                  text: "Pasirinkite žingsnio tipą iš meniu",
                },
                {
                  icon: <DragIndicatorIcon sx={{ fontSize: 20 }} />,
                  text: "Tempkite žingsnius norima tvarka",
                },
              ].map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1.5,
                    borderRadius: adminTheme.borderRadius.md,
                    bgcolor: adminTheme.colors.backgroundAlt,
                    border: `1px solid ${adminTheme.colors.border}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      bgcolor: `${adminTheme.colors.primary}15`,
                      color: adminTheme.colors.primary,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: adminTheme.colors.textMuted,
                        fontSize: "0.7rem",
                      }}
                    >
                      Žingsnis {index + 1}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: adminTheme.colors.textPrimary,
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Visual indicator */}
            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: `1px dashed ${adminTheme.colors.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: adminTheme.colors.primary,
                  animation: "pulse 2s infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1, transform: "scale(1)" },
                    "50%": { opacity: 0.5, transform: "scale(0.8)" },
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: adminTheme.colors.textMuted }}
              >
                Laukiama žingsnių...
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.y, left: contextMenu.x }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              borderRadius: adminTheme.borderRadius.md,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: adminTheme.shadows.lg,
              minWidth: 220,
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${adminTheme.colors.border}` }}>
          <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, fontWeight: 600 }}>
            PRIDĖTI ŽINGSNĮ
          </Typography>
        </Box>
        {availableStepTypes.map((stepType) => (
          <MenuItem
            key={stepType}
            onClick={() => handleStepTypeSelect(stepType)}
            sx={{
              py: 1.5,
              px: 2,
              "&:hover": {
                bgcolor: `${adminTheme.colors.primary}08`,
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: adminTheme.colors.primary,
                minWidth: 40,
              }}
            >
              {getStepTypeIcon(stepType)}
            </ListItemIcon>
            <ListItemText
              primary={getStepTypeLabel(stepType)}
              primaryTypographyProps={{
                fontSize: "0.9rem",
                fontWeight: 500,
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

export default function FlowCanvas(props: FlowCanvasProps) {
  return (
    <ReactFlowProvider>
      <FlowCanvasInner {...props} />
    </ReactFlowProvider>
  );
}
